import { useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, RotateCcw, Trash2 } from "lucide-react";
import { SURVEY_SHORT } from "../../../lib/surveys";

const TYPES = [
  { key: "scale", label: "Scale 1–5" },
  { key: "nps", label: "Recommend 0–10" },
  { key: "single", label: "Pick one" },
  { key: "multi", label: "Pick many" },
  { key: "yesno", label: "Yes / No" },
  { key: "text", label: "Open text" },
];

const inp = "w-full rounded-md border border-border bg-sand px-3 py-2 text-sm text-ink focus:border-ink focus:outline-none";
const blank = () => ({ id: `q_${Math.random().toString(36).slice(2, 7)}`, type: "scale", label: "", required: true, low_label: "Poor", high_label: "Great", options: [] });

const QuestionRow = ({ q, i, n, onChange, onMove, onRemove }) => (
  <li className="rounded-lg border border-border bg-sand p-4" data-testid={`survey-editor-q-${i}`}>
    <div className="flex flex-wrap items-start gap-3">
      <span className="mt-2 font-mono text-[12px] text-olive">{String(i + 1).padStart(2, "0")}</span>
      <div className="min-w-0 flex-1 space-y-3">
        <input value={q.label} onChange={(e) => onChange({ ...q, label: e.target.value })} placeholder="Question text" data-testid={`survey-editor-label-${i}`} className={inp} />
        <div className="flex flex-wrap items-center gap-3">
          <select value={q.type} onChange={(e) => onChange({ ...q, type: e.target.value })} data-testid={`survey-editor-type-${i}`} className="rounded-md border border-border bg-bone px-2 py-1.5 text-sm">
            {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={!!q.required} onChange={(e) => onChange({ ...q, required: e.target.checked })} data-testid={`survey-editor-required-${i}`} /> Required
          </label>
          {q.type === "scale" && (
            <span className="inline-flex items-center gap-2 text-sm text-olive">
              <input value={q.low_label || ""} onChange={(e) => onChange({ ...q, low_label: e.target.value })} placeholder="1 = …" className="w-28 rounded-md border border-border bg-bone px-2 py-1 text-sm" />
              to
              <input value={q.high_label || ""} onChange={(e) => onChange({ ...q, high_label: e.target.value })} placeholder="5 = …" className="w-28 rounded-md border border-border bg-bone px-2 py-1 text-sm" />
            </span>
          )}
        </div>
        {(q.type === "single" || q.type === "multi") && (
          <textarea
            value={(q.options || []).join("\n")}
            onChange={(e) => onChange({ ...q, options: e.target.value.split("\n") })}
            rows={3}
            placeholder="One option per line (at least two)"
            data-testid={`survey-editor-options-${i}`}
            className={`${inp} font-mono text-[13px]`}
          />
        )}
      </div>
      <div className="flex flex-col gap-1">
        <button type="button" onClick={() => onMove(-1)} disabled={i === 0} aria-label="Move up" className="rounded p-1 text-olive hover:bg-ink/5 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
        <button type="button" onClick={() => onMove(1)} disabled={i === n - 1} aria-label="Move down" className="rounded p-1 text-olive hover:bg-ink/5 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
        <button type="button" onClick={onRemove} aria-label="Remove question" data-testid={`survey-editor-remove-${i}`} className="rounded p-1 text-scarlet hover:bg-scarlet/10"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  </li>
);

/** Edit one audience's survey: switch, copy, and the question list. */
export const SurveyEditor = ({ audience, config, defaults, onSave }) => {
  const [draft, setDraft] = useState(() => ({ ...config, questions: config.questions.map((q) => ({ ...q })) }));
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify({ ...config, questions: config.questions.map((q) => ({ ...q })) });
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setQ = (i, q) => setDraft((d) => ({ ...d, questions: d.questions.map((x, j) => (j === i ? q : x)) }));
  const move = (i, dir) => setDraft((d) => {
    const qs = [...d.questions]; const j = i + dir;
    if (j < 0 || j >= qs.length) return d;
    [qs[i], qs[j]] = [qs[j], qs[i]];
    return { ...d, questions: qs };
  });

  const save = async () => {
    setBusy(true);
    try {
      await onSave({ [audience]: { ...draft, questions: draft.questions.map((q) => ({ ...q, options: (q.options || []).map((o) => o.trim()).filter(Boolean) })) } });
      toast.success(`${SURVEY_SHORT[audience]} saved.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the survey.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid={`survey-editor-${audience}`}>
      <div className="flex flex-wrap items-center gap-5">
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
          <input type="checkbox" checked={!!draft.enabled} onChange={(e) => set({ enabled: e.target.checked })} data-testid={`survey-editor-enabled-${audience}`} /> Survey is on
        </label>
        {audience === "student_grant" && (
          <label className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
            <input type="checkbox" checked={!!draft.required_before_final} onChange={(e) => set({ required_before_final: e.target.checked })} data-testid="survey-editor-required-final" /> Required before the final installment (beta)
          </label>
        )}
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        <label className="block"><span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-olive">Title</span><input value={draft.title} onChange={(e) => set({ title: e.target.value })} data-testid={`survey-editor-title-${audience}`} className={`mt-1 ${inp}`} /></label>
        <label className="block"><span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-olive">Intro</span><input value={draft.intro} onChange={(e) => set({ intro: e.target.value })} className={`mt-1 ${inp}`} /></label>
        <label className="block"><span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-olive">Thank-you line</span><input value={draft.thanks} onChange={(e) => set({ thanks: e.target.value })} className={`mt-1 ${inp}`} /></label>
      </div>
      <ul className="space-y-3">
        {draft.questions.map((q, i) => (
          <QuestionRow key={q.id} q={q} i={i} n={draft.questions.length} onChange={(nq) => setQ(i, nq)} onMove={(dir) => move(i, dir)} onRemove={() => set({ questions: draft.questions.filter((_, j) => j !== i) })} />
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-4">
        <button type="button" onClick={() => set({ questions: [...draft.questions, blank()] })} data-testid={`survey-editor-add-${audience}`} className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-4 py-2 text-sm font-semibold text-ink hover:bg-ink hover:text-sand"><Plus className="h-4 w-4" /> Add a question</button>
        <button type="button" onClick={() => set({ questions: defaults.map((q) => ({ ...q })) })} className="inline-flex items-center gap-1.5 text-sm font-semibold text-olive underline-offset-2 hover:text-ink hover:underline"><RotateCcw className="h-3.5 w-3.5" /> Reset to starter questions</button>
        <button type="button" onClick={save} disabled={busy || !dirty} data-testid={`survey-editor-save-${audience}`} className="ml-auto rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-sand hover:bg-forest disabled:opacity-50">Save {SURVEY_SHORT[audience].toLowerCase()}</button>
      </div>
    </div>
  );
};
